// Kalenderdagen in de tijdzone van een Domain of Project — spiegel van ZoneClock in de backend.
// De backend slaat momenten op en levert dagen als 'yyyy-MM-dd' (StartDay/EndDay/DateCloseDay); "vandaag"
// hangt af van de zone van het domain/project, niet van de browser. Zie ook toDateOnlyString (invoer).

// 'yyyy-MM-dd' van vandaag in de zone (IANA-id, bv. Europe/Amsterdam). Zonder (geldige) zone: de browser.
export function zoneToday(timeZoneId: string | null | undefined): string {
    const __now = new Date();
    try {
        // en-CA formatteert als yyyy-MM-dd.
        return new Intl.DateTimeFormat('en-CA', {
            timeZone: timeZoneId ?? undefined,
            year: 'numeric', month: '2-digit', day: '2-digit',
        }).format(__now);
    } catch {
        return dayString(__now);
    }
}

// Dag ('yyyy-MM-dd') → lokale middernacht van díe kalenderdag (voor datepicker/appDate). Niet
// new Date('yyyy-MM-dd'): dat is UTC-middernacht en toont west van UTC de vorige dag.
export function dayToLocalDate(day: string | null | undefined): Date | null {
    if (!day) return null;
    const [__y, __m, __d] = day.slice(0, 10).split('-').map(Number);
    if (!__y || !__m || !__d) return null;
    return new Date(__y, __m - 1, __d);
}

// 'yyyy-MM-dd' plus/min een aantal dagen (kalenderrekenen, geen tijdzone).
export function dayAdd(day: string, days: number): string {
    const __date = dayToLocalDate(day)!;
    __date.setDate(__date.getDate() + days);
    return dayString(__date);
}

// Lokale datumdelen → 'yyyy-MM-dd'.
export function dayString(date: Date): string {
    const __y = String(date.getFullYear()).padStart(4, '0');
    const __m = String(date.getMonth() + 1).padStart(2, '0');
    const __d = String(date.getDate()).padStart(2, '0');
    return `${__y}-${__m}-${__d}`;
}

// Browserzone (IANA-id), voor het standaardland/-zone bij een nieuw domain.
export function browserTimeZoneId(): string | null {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
    } catch {
        return null;
    }
}

// Moment (ISO, UTC) uit de backend ligt in het verleden: tijdzonevrij, gelijk aan de backend-checks
// (EndDate <= nu = verlopen, zie ZoneClock). Voor "nog niet begonnen" juist !momentPassed(start).
export function momentPassed(iso: string | null | undefined): boolean {
    return !!iso && new Date(iso).getTime() <= Date.now();
}

// Moment uit de backend als Date. DateTimes uit de database komen zonder zone-aanduiding (Kind Unspecified, maar
// wel UTC); new Date() zou die als lokale tijd lezen. Zonder 'Z'/offset dus als UTC lezen. Voor API-parameters de
// oorspronkelijke string terugsturen, niet deze Date: de backend vergelijkt met de opgeslagen (zoneloze) waarde.
export function utcMoment(iso: string | null | undefined): Date | null {
    if (!iso) return null;
    const __hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(iso);
    const __date = new Date(__hasZone ? iso : `${iso}Z`);
    return isNaN(__date.getTime()) ? null : __date;
}
